import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Spinner,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React from 'react';
import { TIN_VERDICT } from 'utils/taxValidationApi';

// RAT-1911: renders the tax registry's answer for ONE tax field.
//
// One notice is mounted under each of the two tax inputs and each shows only
// what belongs to it. The registry verdict appears under `result.verdictField`
// alone - the field this country's registry can actually confirm - because a
// single notice under the Tax ID used to carry the VAT number's messages too,
// and for a non-Spanish merchant it showed a verdict about the VAT number
// underneath the KvK field.
//
// The three outcomes are deliberately styled apart:
//  - the registry confirmed the number             -> success / warning
//  - the registry has no record of it (INVALID)    -> error + an explicit confirm
//  - the number was not verified (UNVERIFIED)      -> info, never an error
//
// That last distinction is the point of AC7: presenting "we could not check" as
// a failure trains operators to click through the warning, which would make the
// whole check worthless.
const STATUS_BY_VERDICT = {
  [TIN_VERDICT.VALID]: 'success',
  [TIN_VERDICT.INVALID_SIMILAR]: 'warning',
  [TIN_VERDICT.VALID_REMOVED]: 'warning',
  [TIN_VERDICT.VALID_REVOKED]: 'warning',
  [TIN_VERDICT.INVALID]: 'error',
  [TIN_VERDICT.FORMAT_INVALID]: 'error',
  [TIN_VERDICT.UNVERIFIED]: 'info',
};

// Verdicts that mean the registry actually answered about this field.
//
// The shape hints a field carries are guesses about what the registry will say
// - the Spanish control-character warning says so in as many words. Once the
// registry HAS said something, the guess is settled and printing it anyway
// contradicts the answer: a confirmed number was showing a green tick above the
// words "likely to reject it".
const REGISTRY_ANSWERED = [
  TIN_VERDICT.VALID,
  TIN_VERDICT.INVALID,
  TIN_VERDICT.INVALID_SIMILAR,
  TIN_VERDICT.VALID_REMOVED,
  TIN_VERDICT.VALID_REVOKED,
];

const TaxNumberValidationNotice = ({
  field,
  result,
  isChecking,
  isConfirmed,
  onConfirmChange,
  onAdoptRegistryName,
}) => {
  if (isChecking) {
    return (
      <Box mt="8px" display="flex" alignItems="center" gap="8px">
        <Spinner size="xs" />
        <Text fontSize="sm" color="gray.500">
          Checking the tax number against the tax registry...
        </Text>
      </Box>
    );
  }

  // A field carries either an error or a warning, never both: each of the two
  // fields is graded by one format check that returns one message.
  const errors = result?.fields?.[field]?.errors || [];
  const warnings = result?.fields?.[field]?.warnings || [];

  // The verdict is shown under the one field the registry answered about. A
  // country with no registry behind it says nothing rather than nagging about a
  // check that was never possible.
  const showsVerdict =
    !!result &&
    result.verdictField === field &&
    result.verdict !== TIN_VERDICT.NO_REGISTRY;

  if (!showsVerdict && errors.length === 0 && warnings.length === 0) {
    return null;
  }

  const messages = [];
  if (showsVerdict && result.verdict === TIN_VERDICT.FORMAT_INVALID) {
    // The format errors ARE the explanation, so they replace the generic line.
    messages.push(...(errors.length > 0 ? errors : [result.message]));
  } else if (showsVerdict) {
    messages.push(result.message);
  } else {
    messages.push(...errors);
  }
  // Kept only while the registry has not settled the question - see
  // REGISTRY_ANSWERED. Mixing a hint into an answer also put two severities
  // under one icon, so the warning read as part of the success.
  if (!(showsVerdict && REGISTRY_ANSWERED.includes(result.verdict))) {
    messages.push(...warnings);
  }

  let status = 'warning';
  if (showsVerdict) {
    status = STATUS_BY_VERDICT[result.verdict] || 'info';
  } else if (errors.length > 0) {
    status = 'error';
  }

  return (
    <Box mt="8px">
      <Alert
        status={status}
        borderRadius="12px"
        fontSize="sm"
        alignItems="flex-start"
      >
        <AlertIcon mt="2px" />
        <Box>
          {/* The first line is what happened; anything after it is detail, so
              it carries the weight and the rest reads as subordinate to it. */}
          {messages.map((message, index) => (
            <Text
              key={message}
              fontWeight={index === 0 ? 'medium' : 'normal'}
              mt={index === 0 ? 0 : '4px'}
            >
              {message}
            </Text>
          ))}
          {showsVerdict &&
            result.verdict === TIN_VERDICT.INVALID_SIMILAR &&
            result.registryLegalName && (
              <Box mt="8px">
                <Text color="gray.600">Registry holds this name</Text>
                <Text fontWeight="semibold">{result.registryLegalName}</Text>
                <Button
                  mt="8px"
                  size="xs"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => onAdoptRegistryName(result.registryLegalName)}
                >
                  Use the registry legal name
                </Button>
              </Box>
            )}
          {showsVerdict && result.verdict === TIN_VERDICT.INVALID && (
            <Checkbox
              mt="10px"
              alignItems="flex-start"
              isChecked={isConfirmed}
              onChange={(event) => onConfirmChange(event.target.checked)}
            >
              {/* The registry graded the number and the legal name as a pair, so
                  confirming it stores both as entered - saying only "tax number"
                  hid half of what the operator is signing off on. */}
              <Text fontSize="sm">
                {result.checkedWithLegalName
                  ? 'Save this tax number and legal name as entered'
                  : 'Save this tax number as entered'}
              </Text>
            </Checkbox>
          )}
        </Box>
      </Alert>
    </Box>
  );
};

TaxNumberValidationNotice.propTypes = {
  /** Which of the two tax fields this notice sits under. */
  field: PropTypes.oneOf(['taxId', 'vatNumber']).isRequired,
  result: PropTypes.shape({
    verdict: PropTypes.string,
    verdictField: PropTypes.oneOf(['taxId', 'vatNumber']),
    checkedWithLegalName: PropTypes.bool,
    message: PropTypes.string,
    registryLegalName: PropTypes.string,
    fields: PropTypes.shape({
      taxId: PropTypes.shape({
        errors: PropTypes.arrayOf(PropTypes.string),
        warnings: PropTypes.arrayOf(PropTypes.string),
      }),
      vatNumber: PropTypes.shape({
        errors: PropTypes.arrayOf(PropTypes.string),
        warnings: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
  }),
  isChecking: PropTypes.bool,
  isConfirmed: PropTypes.bool,
  onConfirmChange: PropTypes.func.isRequired,
  onAdoptRegistryName: PropTypes.func.isRequired,
};

export default TaxNumberValidationNotice;
